import { gql } from '@apollo/client';
import DashboardCard from 'components/DashboardCard';
import SurveyTypeTag from 'components/SurveyTypeTag';
import dayjs from 'dayjs';
import { useQuery } from 'hooks/apollo';
import useDataSource from 'hooks/useDataSource';
import React, { useMemo } from 'react';
import { FormattedRelativeTime } from 'react-intl';
import { generatePath, useRouteMatch } from 'react-router-dom';
import { getDefaultDateRange } from 'shared/constants/date';
import { percent, round } from 'shared/constants/math';
import { EDIT_SURVEY } from 'shared/constants/permissions';
import selectUnit from 'shared/utils/selectUnit';

function getData(title, graph) {
  if (graph?.segments?.[0]?.values?.[0]) {
    const { value, data } = graph.segments[0].values[0];

    if (value > 0) {
      return {
        title,
        value,
        chartData: data,
      };
    }
  }
  return {
    title,
    value: 0,
    chartData: [],
  };
}

function getStats(results, surveyGroup) {
  const viewsStats = getData('Views', results.graphs.find(g => g.title === 'views'));
  const responsesStats = getData('Responses', results.graphs.find(g => g.title === 'responses'));
  const lastResponse = surveyGroup.surveys.reduce((d, s) => (!d || (s.lastResponse && dayjs(s.lastResponse).isAfter(d)) ? s.lastResponse : d), null);

  const stats = [
    viewsStats,
    responsesStats,
    {
      title: 'Response rate',
      value: round(percent(responsesStats.value, viewsStats.value)),
      suffix: '%',
      chartData: responsesStats.chartData.map((v, i) => round(percent(v, viewsStats.chartData[i]))),
    },
    {
      title: 'Last response',
      value: lastResponse && dayjs(lastResponse).toDate(),
      formatter: time => (time ? <FormattedRelativeTime {...selectUnit(time)} /> : '-'),
    },
  ];

  return stats;
}

const QUERY = gql`
  query($input: ResultInput) {
    result(input: $input)
  }
`;

const SurveyGroupCard = ({ surveyGroup }) => {
  const { path, params } = useRouteMatch('/data-sources/:dataSourceId/:module/:page?/:surveyGroupId?');
  const { hasDataSourcePermission } = useDataSource();
  const surveyGroupId = surveyGroup?.id;
  const { data: { result } = {}, loading } = useQuery(QUERY, {
    variables: {
      input: {
        filter: {
          dateRange: getDefaultDateRange(),
          surveyGroupId,
          filterSegments: [
            {
              friendlyTitle: 'All',
              filterOptions: [],
            },
          ],
          benchmarkSegments: [],
        },
        settings: {
          responseMeta: [
            'responses',
            'views',
          ],
        },
        multiple: true,
      },
    },
  });

  const stats = useMemo(() => result && getStats(result, surveyGroup), [result, surveyGroup]);

  return (
    <DashboardCard
      loading={loading}
      title={surveyGroup.title}
      extra={[<SurveyTypeTag key="type" type={surveyGroup.type} />]}
      period="Last 30 days"
      resultsUrl={generatePath(path, { ...params, page: 'results', surveyGroupId })}
      manageUrl={hasDataSourcePermission(EDIT_SURVEY) && surveyGroup.surveys.length > 0 && generatePath(path, { ...params, page: 'edit', surveyGroupId })}
      manageLabel="Edit survey"
      statistics={stats}
    />
  );
};

export default SurveyGroupCard;
